import React from 'react';
import { autobind } from 'core-decorators';
import { Collapse, Card, CardTitle, CardSubtitle } from 'reactstrap';
import LucideIcon from '../lucide-icon';
import Info from '../info';
import { SearchQueryContext } from '../filtro/FiltroDrawerNew';
import {
  findLabelsInChildren,
  filterChildren,
  extractHeaderText,
  normalizeText,
} from '../filtro/searchUtils';

@autobind
class CardCustom extends React.Component {
  static contextType = SearchQueryContext;

  static defaultProps = {
    open: true,
    borderColor: '#cfdbe2',
    overflow: false,
    showDelete: true,
    showNew: true,
    showPage: true,
    showToolBar: true,
    showDetail: false,
    showSearch: true,
    errors: null,
    collapseCallback: () => {},
    hideShadow: false,
    childrenLoaded: false,
    noPadding: false,
    ehAccordionDrawer: false,
    headerActions: [],
    collapsible: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      open: this.props.open,
      _prevSearchQuery: '',
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (newProps.open !== this.props.open) {
      this.setState({ open: newProps.open });
    }
  }

  _getSearchContext() {
    const ctx = this.context;
    if (!ctx || typeof ctx !== 'object')
      return { searchQuery: null, intl: null };
    return { searchQuery: ctx.searchQuery || null, intl: ctx.intl || null };
  }

  componentDidUpdate(prevProps, prevState) {
    // Mantém sincronizado com prop open
    if (
      this.props.open !== prevProps.open &&
      this.props.open !== this.state.open
    ) {
      this.setState({ open: this.props.open });
    }

    // Busca via context o auto-open quando dá match, só processa se o context existe
    const { searchQuery, intl } = this._getSearchContext();
    const prevSearchQuery = prevState._prevSearchQuery || '';

    if (searchQuery !== null && searchQuery !== prevSearchQuery) {
      if (searchQuery && searchQuery.trim()) {
        const hasMatch = findLabelsInChildren(
          this.props.children,
          searchQuery,
          intl
        );
        let headerMatches = false;
        if (this.props.header) {
          const headerText = extractHeaderText(this.props.header);
          headerMatches = normalizeText(headerText).includes(
            normalizeText(searchQuery)
          );
        }

        if ((hasMatch || headerMatches) && !this.state.open) {
          this.setState({ open: true, _prevSearchQuery: searchQuery });
        } else {
          this.setState({ _prevSearchQuery: searchQuery });
        }
      } else {
        this.setState({ _prevSearchQuery: '' });
      }
    }
  }

  handleCollapse() {
    if (!this.props.collapsible) {
      return;
    }
    this.props.onCollapse && this.props.onCollapse();
    this.setState(
      {
        open: !this.state.open,
        childrenLoaded: true,
      },
      () => {
        this.props.collapseCallback(this.state.open);
      }
    );
  }

  render() {
    let {
      header,
      children,
      subtitle,
      headerStyle,
      errors,
      hideShadow,
      loadChildrenOnOpen = false,
      style,
      styleTitle,
      collapsible,
    } = this.props;

    const isOpen = collapsible ? this.state.open : true;

    // filtra children e esconde se não deu match
    const { searchQuery, intl: searchIntl } = this._getSearchContext();

    if (searchQuery && searchQuery.trim()) {
      const hasMatch = findLabelsInChildren(children, searchQuery, searchIntl);
      let headerMatches = false;
      if (header) {
        const headerText = extractHeaderText(header);
        headerMatches = normalizeText(headerText).includes(
          normalizeText(searchQuery)
        );
      }

      // Se não há match nem no header nem nos children, esconde o CardCustom
      if (!hasMatch && !headerMatches) {
        return null;
      }

      // Filtra children para mostrar apenas os que correspondem
      if (hasMatch) {
        const filtered = filterChildren(children, searchQuery, searchIntl);
        children = filtered || children;
      }
    }

    return (
      <Card
        body
        style={{
          marginBottom: 15,
          cursor: 'pointer',
          borderColor: errors ? 'red' : null,
          boxShadow: hideShadow ? 'none' : '',
          ...(this.props.ehAccordionDrawer
            ? {
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
                padding: 0,
                marginTop: 20,
                marginBottom: 0,
              }
            : {}),
          ...style,
        }}
        className={`${this.props.className} ${
          this.props.ehAccordionDrawer
            ? ''
            : this.props.noPadding
            ? ''
            : 'px-4 py-3'
        }`}
      >
        {header && (
          <>
            <CardTitle
              onClick={collapsible ? this.handleCollapse : undefined}
              className={headerStyle}
              style={{
                ...(this.props.ehAccordionDrawer
                  ? {
                      color: 'var(--primary)',
                      fontWeight: 700,
                      fontSize: '16px',
                      cursor: collapsible ? 'pointer' : 'default',
                      paddingBottom: 6,
                    }
                  : {}),
                ...(styleTitle || {}),
                ...(!collapsible ? { cursor: 'default' } : {}),
              }}
            >
              {header}
              {this.props.textInfoTitle && (
                <Info
                  className="ml-1"
                  id={`info-title-panel-${header}`}
                  placement="right"
                  text={this.props.textInfoTitle}
                />
              )}
              {this.props.headerActions}
              {collapsible && (
                <LucideIcon
                  icon={isOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={this.props.ehAccordionDrawer ? 20 : 16}
                  color={
                    this.props.ehAccordionDrawer ? 'var(--primary)' : undefined
                  }
                  className={`pull-right${
                    this.props.headerActions ? ' ml-auto flex-shrink-0' : ''
                  }`}
                />
              )}
            </CardTitle>
            {subtitle && (
              <CardSubtitle className="mt-1 text-muted">
                {subtitle}
              </CardSubtitle>
            )}
          </>
        )}
        <Collapse
          isOpen={isOpen}
          style={{
            marginTop: this.props.ehAccordionDrawer
              ? 0
              : isOpen
              ? this.props.contentMarginTop !== undefined
                ? this.props.contentMarginTop
                : 28
              : 0,
            transition: '0.3s ease-out',
            cursor: 'default',
          }}
        >
          {!loadChildrenOnOpen || isOpen || this.state.childrenLoaded
            ? children
            : null}
        </Collapse>
      </Card>
    );
  }
}

export default CardCustom;
